<template>
    <div
        ref="container"
        class="sort-menu"
        data-expect-mismatch
        tabindex="0"
        @click.prevent.stop="toggle"
        @keyup.enter.prevent.stop="openWithKeyboard"
    >
        <span class="label">
            <span class="prefix"
                >{{ t('catalogue.filters.sort_by') }}:&nbsp;</span
            >
            {{ selectedItemLabel }}
        </span>

        <div class="sort-options">
            <div
                v-if="isMenuOpen"
                role="menu"
                class="sorting-menu"
            >
                <ThLink
                    v-for="(option, index) in optionsWithId"
                    :id="option.id"
                    :key="index"
                    ref="items"
                    :class="getItemClasses(option.option)"
                    class="sorting-menu-item"
                    role="menuitemcheckbox"
                    :to="routeGetter(option.option.value)"
                    @keyup.escape.prevent.stop="closeWithKeyboard"
                    @click.prevent.stop="closeWithMouse"
                >
                    {{ option.option.label }}
                </ThLink>
            </div>
        </div>
    </div>
</template>

<script
    lang="ts"
    setup
    generic="
        T extends
            | CatalogueSortOrder
            | NonSkuGemstoneSortInput
            | NonSkuLabGrownDiamondSortInput
            | GemsApi_StoneSorting
    "
>
import { computed, nextTick, ref, useTemplateRef, type VNode } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { isDeepEqual } from 'remeda'

import {
    CatalogueSortOrder,
    type GemsApi_StoneSorting,
    type NonSkuGemstoneSortInput,
    type NonSkuLabGrownDiamondSortInput,
} from '@/graphql/api'

import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import { useIsMobile } from '@/composables/app/useIsMobile'
import { getDocument, getTimeout } from '@/helpers/globals'

import ThLink from '@/components/ui/buttons/ThLink.vue'

export interface DropdownOption<T> {
    value: T
    label: string
}

const { t } = useI18n()

const props = defineProps<{
    model: T | undefined | null
    options: DropdownOption<T>[]
    routeGetter: (model: T) => ButtonRouteRelative | string
}>()

const container = ref<HTMLDivElement | undefined>()
const items = useTemplateRef<VNode[] | undefined>('items')
const isMenuOpen = ref(false)

const toggle = () => {
    isMenuOpen.value = !isMenuOpen.value
}

const close = () => {
    isMenuOpen.value = false
}

const optionsWithId = computed(() =>
    props.options.map((option) => ({
        option,
        id: option.label.replaceAll(' ', '-'),
    }))
)

const document = getDocument()

const openWithKeyboard = () => {
    isMenuOpen.value = true

    nextTick(() => {
        if (!document || !optionsWithId.value[0]?.id) {
            return
        }

        document.getElementById(optionsWithId.value[0]?.id)?.focus()
    })
}

const closeWithKeyboard = () => {
    nextTick(() => {
        close()

        container.value?.focus()
    })
}

const setTimeout = getTimeout()?.setTimeout

const closeWithMouse = () => {
    nextTick(() => {
        close()

        setTimeout?.(() => {
            container.value?.focus()
        })
    })
}

onClickOutside(container, close)

const isSelected = (option: DropdownOption<T>): boolean => {
    if (
        props.model &&
        (typeof option.value === 'object' || typeof props.model === 'object')
    ) {
        return isDeepEqual(option.value, props.model)
    }

    return option.value === props.model
}

const { isMobile } = useIsMobile()

const selectedItemLabel = computed(() => {
    const selectedItem = props.options.find((option) => isSelected(option))

    if (
        selectedItem?.value === CatalogueSortOrder.Recommended &&
        isMobile.value
    ) {
        return t('catalogue.filters.sort_by')
    }

    return selectedItem?.label
})

const getItemClasses = (option: DropdownOption<T>) => ({
    'selected': isSelected(option),
})
</script>

<style lang="scss" scoped>
.sort-menu {
    display: block;
    position: relative;
    margin-left: 20px;

    .label {
        font-family: $effra-font-stack;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        font-weight: 400;
        font-size: 0.875rem;
        cursor: pointer;
        display: flex;
        align-items: center;

        .prefix {
            display: none;

            @include min-width(640) {
                display: inline;
            }
        }

        &::after {
            font-family: $icon-font;
            content: $icon-angle-down;
            display: block;
            margin-left: 5px;
            font-weight: 600;
            font-size: 0.7rem;
        }
    }

    .sorting-menu {
        position: absolute;
        z-index: 10;
        top: 34px;
        right: 0;
        background: $white;
        font-family: $effra-font-stack;
        letter-spacing: 0.05em;
        font-weight: 400;
        font-size: 0.8rem;
        padding: 5px;
        width: 200px;
        border-radius: 3px;
        text-align: right;
        border: 1px solid $grey-200;

        .sorting-menu-item {
            display: block;
            padding: 10px 15px;
            cursor: pointer;
            border-radius: 3px;
            color: $slate;

            @include hover() {
                background: $stone-2;
            }

            &.selected {
                background: $stone-2;
            }
        }
    }
}
</style>
